<svelte:options
  customElement={{
    tag: "oc-focused-dialog-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      inApp: { type: "Boolean", reflect: true },
      headline: { type: "String", reflect: true },
      headlineLevel: { type: "Number", attribute: "headline-level" },
      closeUrl: { type: "String", attribute: "close-url", reflect: true },
      prevUrl: { type: "String", attribute: "prev-url", reflect: true },
    },
  }}
/>

<script lang="ts">
  import { toggle } from "@otto-ec/global-resources/toggle";
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { onMount } from "svelte";
  import type { Props, Events } from "./FocusedDialogV1.types.js";
  import {
    isSafeUrl,
    closeParam,
    prefix,
    getFirstAvailableParam,
  } from "./FocusedDialogV1.utils.js";
  import { useSlots } from "../../../common/utils/useSlots.svelte.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { useStuckState } from "./lib/useStuckState.svelte.js";
  import { useInAppDetector } from "./lib/useInAppDetector.js";

  const base64HrefAttribute = "base64-href";
  const toggleName = "oc_focused_dialog_v1_hide_prev_in_app";

  const prevQuery = `[slot='actions'] [data-${prefix}\\.prev-url]`;

  let { closeUrl, prevUrl, headline = "", inApp = false, headlineLevel = 1 }: Props = $props();

  const headlineElement = $derived(headlineLevel ? `h${headlineLevel}` : "div");

  /*                                                                                     */
  closeUrl = getFirstAvailableParam([`${prefix}.close-url`, "focussedDialogCloseUrl"], closeUrl);
  /*                                                                                    */
  prevUrl = getFirstAvailableParam([`${prefix}.prev-url`, "focussedDialogPrevUrl"], prevUrl);

  const Host = $host();
  const slots = useSlots(Host);
  const dispatch = useEventDispatcher<Events>(Host);

  const stuckState = useStuckState();

  let stickyTitleInApp = $state(false);
  useInAppDetector(
    (a) => (inApp = a),
    (s) => (stickyTitleInApp = s),
  );

  /*                                      */
  function handleClose(): void {
    dispatch("oc-focused-dialog-close", undefined, { alias: ["oc-close"] });
    if (closeUrl && isSafeUrl(closeUrl)) {
      location.assign(closeUrl);
    }
  }

  function handlePrev(): void {
    if (prevUrl && isSafeUrl(prevUrl)) {
      location.assign(prevUrl);
    }
  }

  onMount(() => {
    /*                                                        */

    if (prevUrl && isSafeUrl(prevUrl)) {
      const prevElements = Array.from(
        Host.querySelectorAll(prevQuery) as NodeListOf<HTMLOcButtonV1Element | HTMLAnchorElement>,
      );

      prevElements.forEach((el) => {
        if (el.hasAttribute(base64HrefAttribute)) {
          /*                                     */
          el.setAttribute(base64HrefAttribute, window.btoa(prevUrl as string));
        } else {
          el.href = prevUrl;
        }
      });
    }

    if (closeUrl && isSafeUrl(closeUrl)) {
      const linkAnchorUpdateObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          Array.from(mutation.addedNodes)
            .filter((node): node is HTMLAnchorElement => node instanceof HTMLElement)
            .forEach((node) => {
              if (node.hasAttribute(`data-${prefix}.use-close-url`)) {
                if (node.hasAttribute(base64HrefAttribute)) {
                  const newCloseUrl = new URL(
                    window.atob(node.getAttribute(base64HrefAttribute) || ""),
                    location.href,
                  );
                  newCloseUrl.searchParams.set(closeParam, closeUrl as string);
                  node.setAttribute(base64HrefAttribute, window.btoa(newCloseUrl.href));
                } else {
                  const { href } = node;
                  if (!href) return;
                  const newCloseUrl = new URL(href as string, location.href);
                  newCloseUrl.searchParams.set(closeParam, closeUrl as string);

                  node.setAttribute("href", newCloseUrl.href);
                }
              }
            });
        });
      });

      /*                */
      linkAnchorUpdateObserver.observe(Host, { childList: true, subtree: true });
      return () => linkAnchorUpdateObserver.disconnect();
    }
  });
</script>

<div
  class="focused-dialog"
  class:focused-dialog--inApp={inApp}
  class:focused-dialog--sticky-title={stickyTitleInApp}
>
  <div
    bind:this={stuckState.title.element}
    class="title"
    class:title--stuck={stuckState.title.stuck}
  >
    <div
      class="title__prev"
      class:title__prev--hidden={inApp && toggle.get(toggleName, false) && !stickyTitleInApp}
    >
      {#if prevUrl && isSafeUrl(prevUrl)}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <oc-icon-button-v3 oc-aria-label="zurück" icon="arrow-left" onclick={handlePrev}
        ></oc-icon-button-v3>
      {/if}
    </div>

    <slot name="headline">
      <svelte:element this={headlineElement} class="title__headline">{headline}</svelte:element>
    </slot>

    <div class="title__close">
      {#if closeUrl && isSafeUrl(closeUrl)}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <oc-icon-button-v3 icon="close" onclick={handleClose} oc-aria-label="schließen">
        </oc-icon-button-v3>
      {/if}
    </div>
  </div>

  <section class="content-area">
    <slot />
  </section>
  {#if slots.actions}
    <div
      class="actions"
      bind:this={stuckState.actions.element}
      class:actions--stuck={stuckState.actions.stuck}
    >
      <slot name="actions" />
    </div>
  {/if}
</div>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins" as mixins;
  :global {
    :host {
      --content-background-color: initial;
      --content-padding: initial;
      --component-background-color: var(--oc-semantic-color-canvas-background);
    }

    .focused-dialog {
      background-color: var(
        --component-background-color,
        tokens.$oc-component-focused-dialog-background-color
      );

      height: 100%;

      .title {
        display: flex;
        position: static;
        z-index: 2; /*                                                                               */
        top: -2px;
        justify-content: space-between;
        background-color: tokens.$oc-component-focused-dialog-header-background-color;
        padding: 24px 16px 16px;

        &__prev,
        &__close {
          /**
 *
 *
 */
          @include mixins.breakpoint-from-m($to: false, $containerFirst: false) {
            width: tokens.$oc-base-dimension-relative-32; /*                           */
          }
        }

        /**
 *
 */

        &__prev--hidden {
          @include mixins.visually-hidden;
        }

        &__headline {
          text-align: left;
          flex-grow: 1;
          margin: 0;
          padding-top: tokens.$oc-component-focused-dialog-header-title-spacing-top;
          color: tokens.$oc-component-focused-dialog-header-title-color;
          word-break: break-word;
          font: tokens.$oc-component-focused-dialog-header-title-font;
          @include mixins.breakpoint-from-m($to: false, $containerFirst: false) {
            text-align: center;
          }
        }
      }

      /**
 *
 */
      &:not(.focused-dialog--inApp),
      &.focused-dialog--sticky-title {
        .title {
          position: sticky;
          border-bottom: tokens.$oc-component-focused-dialog-header-conditional-border-bottom-width
            solid transparent;
          top: -2px;

          &--stuck {
            border-color: tokens.$oc-component-focused-dialog-header-conditional-border-bottom-color;
          }
        }
      }

      .content-area {
        padding: var(--content-padding, 16px);
        background-color: var(
          --content-background-color,
          tokens.$oc-component-focused-dialog-content-background-color
        );
      }

      .actions {
        background-color: tokens.$oc-component-focused-dialog-action-bar-background-color;
        padding: tokens.$oc-component-focused-dialog-action-bar-spacing-top
          tokens.$oc-component-focused-dialog-action-bar-spacing-x
          tokens.$oc-component-focused-dialog-action-bar-spacing-bottom
          tokens.$oc-component-focused-dialog-action-bar-spacing-x;
        bottom: -1px;
        position: sticky;
        z-index: 1;

        &--stuck {
          border-top: tokens.$oc-component-focused-dialog-action-bar-conditional-border-top-width
            solid tokens.$oc-component-focused-dialog-action-bar-conditional-border-top-color;
        }
      }
    }
  }
</style>
